<template>
  <div class="overflow-x-auto">
    <TableLight class="w-full min-w-[36rem] table-fixed [&_td]:align-middle">
      <colgroup>
        <col class="w-22" />
        <col />
        <col class="w-32" />
        <col class="w-42" />
        <col v-if="compareEnabled" class="w-16" />
      </colgroup>
      <thead>
        <tr>
          <TableH
            :label="t('ProductTop100Ranking.table.rank')"
            :sort-params="sortState(ManageTopProductsRankingOrderBy.Rank)"
            @sort="emit('sort', $event)"
          />
          <TableH
            :label="t('ProductTop100Ranking.table.tool')"
            :sort-params="sortState(ManageTopProductsRankingOrderBy.ToolTitle)"
            @sort="emit('sort', $event)"
          />
          <TableH
            :label="t('ProductTop100Ranking.table.stars')"
            :sort-params="sortState(ManageTopProductsRankingOrderBy.Rating)"
            @sort="emit('sort', $event)"
          />
          <TableH
            :label="t('ProductTop100Ranking.table.reviews')"
            :sort-params="sortState(ManageTopProductsRankingOrderBy.ReviewCount)"
            @sort="emit('sort', $event)"
          />
          <th v-if="compareEnabled" scope="col">
            <span class="sr-only">{{ t('ProductTop100Ranking.compare.add') }}</span>
          </th>
        </tr>
      </thead>
      <tbody v-if="ownEntry" data-testid="top-products-ranking-pinned">
        <tr>
          <td :colspan="colCount" class="border-b-0! bg-surface-elevation-2 txt-label-s-bold-uppercase text-subtle">
            <span class="flex items-center gap-1">
              <PinIcon class="lucide-icon-sm" />
              {{ t('ProductTop100Ranking.table.pinned') }}
            </span>
          </td>
        </tr>
        <tr class="bg-primary-subtle text-primary [&>td]:border-b-0!" data-testid="top-products-ranking-own-row">
          <td class="txt-body-s-bold tabular-nums">{{ ownEntry.rank ?? '–' }}</td>
          <td class="txt-body-s-bold">
            <span class="block truncate">{{ ownEntry.toolTitle }}</span>
          </td>
          <td>
            <span class="inline-flex items-center gap-1">
              <StarIcon class="lucide-icon-sm text-icon-secondary" fill="currentColor" />
              {{ formatStars(ownEntry.rating) }}
            </span>
          </td>
          <td class="text-right tabular-nums">{{ formatCount(ownEntry.reviewCount) }}</td>
          <td v-if="compareEnabled"></td>
        </tr>
        <tr aria-hidden="true" data-testid="top-products-ranking-spacer">
          <td :colspan="colCount" class="h-6 border-b-0! p-0"></td>
        </tr>
      </tbody>
      <tbody>
        <tr v-if="!entries.length" data-testid="top-products-ranking-empty">
          <td :colspan="colCount" class="py-6 text-center text-subtle">
            {{ searchTerm ? t('ProductTop100Ranking.table.noResult', { term: searchTerm }) : t('ProductTop100Ranking.table.empty') }}
          </td>
        </tr>
        <tr
          v-for="entry in entries"
          :key="entry.tool"
          :class="{ 'bg-surface-elevation-2 text-subtle': isOwn(entry) }"
          :data-testid="isOwn(entry) ? 'top-products-ranking-own-duplicate' : 'top-products-ranking-row'"
        >
          <td class="tabular-nums">{{ entry.rank ?? '–' }}</td>
          <td :class="isOwn(entry) ? 'text-body-s' : 'text-body-s-bold'" data-testid="top-products-ranking-row-title">
            <span class="block truncate">{{ entry.toolTitle }}</span>
            <span v-if="isOwn(entry)" class="sr-only">{{ t('ProductTop100Ranking.table.alreadyShown') }}</span>
          </td>
          <td>
            <span class="inline-flex items-center gap-1">
              <StarIcon class="lucide-icon-sm text-icon-secondary" :class="{ 'opacity-60': isOwn(entry) }" fill="currentColor" />
              {{ formatStars(entry.rating) }}
            </span>
          </td>
          <td class="text-right tabular-nums">{{ formatCount(entry.reviewCount) }}</td>
          <td v-if="compareEnabled" class="text-right">
            <TooltipWrapper v-if="!isOwn(entry)" class="ml-auto">
              <template #trigger>
                <IconButton
                  v-if="!isSelected?.(entry.tool)"
                  class="text-icon-success!"
                  :icon="PlusIcon"
                  size="extra-small"
                  variant="ghost-neutral"
                  :aria-label="t('ProductTop100Ranking.compare.add')"
                  data-testid="top-products-ranking-compare-add"
                  @click="emit('compare-add', entry)"
                />
                <IconButton
                  v-else
                  class="text-icon-error!"
                  :icon="XIcon"
                  size="extra-small"
                  variant="ghost-neutral"
                  :aria-label="t('ProductTop100Ranking.compare.remove')"
                  data-testid="top-products-ranking-compare-remove"
                  @click="emit('compare-remove', entry.tool)"
                />
              </template>
              <template #content>
                {{ isSelected?.(entry.tool) ? t('ProductTop100Ranking.compare.remove') : t('ProductTop100Ranking.compare.add') }}
              </template>
            </TooltipWrapper>
          </td>
        </tr>
      </tbody>
    </TableLight>
  </div>
</template>

<script setup lang="ts">
import { IconButton, TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { PinIcon, PlusIcon, StarIcon, XIcon } from '@ramp106/omrjs-core-ui/icons'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { ManageTopProductsRankingOrderBy } from '@/gql/reviews'
import TableH from '@/ui/TableLight/TableH.vue'
import TableLight from '@/ui/TableLight/TableLight.vue'
import type { SortDirection, SortParams } from '@/ui/TableLight/types'

import type { TopProductsRankingEntry } from './types'

const props = defineProps<{
  entries: TopProductsRankingEntry[]
  ownEntry?: TopProductsRankingEntry
  sortBy: ManageTopProductsRankingOrderBy
  sortDirection: SortDirection
  searchTerm?: string
  compareEnabled?: boolean
  isSelected?: (tool: string) => boolean
}>()

const emit = defineEmits<{
  (e: 'sort', params: SortParams<ManageTopProductsRankingOrderBy, SortDirection>): void
  (e: 'compare-add', entry: TopProductsRankingEntry): void
  (e: 'compare-remove', tool: string): void
}>()

const colCount = computed(() => (props.compareEnabled ? 5 : 4))

const { t, n } = useI18n()

function sortState(key: ManageTopProductsRankingOrderBy): SortParams<ManageTopProductsRankingOrderBy, SortDirection> {
  return { sortBy: key, sortDirection: props.sortBy === key ? props.sortDirection : undefined }
}

function isOwn(entry: TopProductsRankingEntry): boolean {
  return !!props.ownEntry && entry.tool === props.ownEntry.tool
}

function formatStars(rating: number | null | undefined): string {
  return rating == null ? '–' : n(rating, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

function formatCount(count: number | null | undefined): string {
  return count == null ? '–' : n(count)
}
</script>
